import React from 'react';
import ModeSelector from './components/ModeSelector';
import AddressSearch from './components/AddressSearch';
import DateTimePicker from './components/DateTimePicker';
import ItineraryCard from './components/ItineraryCard';
import RouteMap from './components/RouteMap';
import RouteSliders from './components/RouteSliders';
import DepartureBoard from './components/DepartureBoard';
import StopPicker from './components/StopPicker';
import { useTransitPlanner } from './hooks/useTransitPlanner';
import { fetchStops } from './utils/api';
import type { Stop } from './types/api';

export default function App() {
  const planner = useTransitPlanner();
  const [departureStop, setDepartureStop] = React.useState<Stop | null>(null);
  const [userLocation, setUserLocation] = React.useState<{ lat: number; lon: number } | null>(null);

  // Auto-detect user location on mount
  React.useEffect(() => {
    const cached = sessionStorage.getItem('userLocation');
    if (cached) {
      try {
        setUserLocation(JSON.parse(cached));
      } catch { /* ignore */ }
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLocation(loc);
        sessionStorage.setItem('userLocation', JSON.stringify(loc));
      },
      () => { /* ignore geolocation denial */ },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleUseCurrentLocation = React.useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLocation(loc);
        sessionStorage.setItem('userLocation', JSON.stringify(loc));
        planner.setOrigin(loc);
      },
      () => { /* ignore */ },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [planner.setOrigin]);

  const handleMapClick = React.useCallback(
    (loc: { lat: number; lon: number }) => {
      if (!planner.origin) {
        planner.setOrigin(loc);
      } else if (!planner.destination) {
        planner.setDestination(loc);
      }
    },
    [planner.origin, planner.destination, planner.setOrigin, planner.setDestination]
  );

  const handleStopSelect = React.useCallback(async (stop: Stop) => {
    setDepartureStop(stop);
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <h1>Twin Cities Transit Planner</h1>
        <ModeSelector mode={planner.mode} setMode={planner.setMode} />
      </header>

      <div className="app-body">
        <aside className="sidebar">
          <AddressSearch
            label="Origin"
            value={planner.origin}
            onChange={planner.setOrigin}
            showLocButton={true}
            onLocClick={handleUseCurrentLocation}
            userLocation={userLocation}
            placeholder="From address"
          />
          <AddressSearch
            label="Destination"
            value={planner.destination}
            onChange={planner.setDestination}
            userLocation={userLocation}
            nearPoint={planner.origin}
            placeholder="To address"
          />
          <DateTimePicker
            date={planner.date}
            time={planner.time}
            arriveBy={planner.arriveBy}
            setDate={planner.setDate}
            setTime={planner.setTime}
            setArriveBy={planner.setArriveBy}
          />
          <RouteSliders
            maxTransfers={planner.maxTransfers}
            setMaxTransfers={planner.setMaxTransfers}
            bikeTransferWeight={planner.bikeTransferWeight}
            setBikeTransferWeight={planner.setBikeTransferWeight}
            bikeSafety={planner.bikeSafety}
            setBikeSafety={planner.setBikeSafety}
          />
          <button
            className="search-btn"
            onClick={planner.search}
            disabled={planner.loading || !planner.origin || !planner.destination}
          >
            {planner.loading ? 'Searching...' : 'Search Routes'}
          </button>

          {planner.error && <div className="error">{planner.error}</div>}

          <div className="itinerary-list">
            {planner.itineraries.map((it, i) => (
              <ItineraryCard
                key={i}
                itinerary={it}
                selected={planner.selectedItinerary === it}
                onSelect={() => planner.selectItinerary(it)}
              />
            ))}
          </div>

          <StopPicker onSelect={handleStopSelect} />
        </aside>

        <main className="map-area">
          <RouteMap
            origin={planner.origin}
            destination={planner.destination}
            selectedItinerary={planner.selectedItinerary}
            setOrigin={planner.setOrigin}
            setDestination={planner.setDestination}
          />
        </main>
      </div>

      {departureStop && (
        <DepartureBoard
          stopId={departureStop.gtfsId}
          stopName={departureStop.name}
          onClose={() => setDepartureStop(null)}
        />
      )}
    </div>
  );
}
